import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Shield, Bell, Lock, Trash2, Sparkles, History, Star, Download } from "lucide-react";
import { useEffect } from "react";

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user === false)) navigate("/login", { state: { from: "/account" } });
  }, [user, loading, navigate]);

  if (!user || user === false) return null;

  const initials = (user.name || user.email || "?")[0].toUpperCase();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-12" data-testid="account-page">
        {/* Hero card */}
        <div className="rounded-2xl border border-[var(--omni-border)] p-8 bg-gradient-to-br from-white to-[var(--omni-surface)]">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[var(--omni-accent)] text-white flex items-center justify-center text-4xl font-black">
              {initials}
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Bonjour {user.name}</h1>
              <p className="text-gray-600 mt-1">{user.email} · <span className="text-xs uppercase font-mono">{user.role}</span></p>
              <p className="text-xs text-gray-400 mt-1">Compte créé le {new Date(user.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Categories like Google Account home */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {[
            { i: Shield, t: "Sécurité", d: "Vérification, mots de passe, sessions", to: "/passwords", testid: "card-security" },
            { i: Bell, t: "Confidentialité", d: "Données, historique, contrôles", to: "/history", testid: "card-privacy" },
            { i: Lock, t: "Données", d: "Téléchargements, exportations", to: "/downloads", testid: "card-data" },
            { i: Sparkles, t: "IA personnelle", d: "Préférences, contexte", to: "/settings", testid: "card-ai" },
            { i: Star, t: "Favoris", d: "Sites sauvegardés", to: "/bookmarks", testid: "card-favorites" },
            { i: History, t: "Historique", d: "Vos dernières recherches", to: "/history", testid: "card-history" },
          ].map(({ i: Icon, t, d, to, testid }) => (
            <a
              key={t}
              href={to}
              onClick={(e) => { e.preventDefault(); navigate(to); }}
              data-testid={testid}
              className="p-6 rounded-xl border border-[var(--omni-border)] hover:border-black transition group"
            >
              <Icon className="text-[var(--omni-accent)] mb-3" size={22} />
              <h3 className="font-bold mb-1 group-hover:underline">{t}</h3>
              <p className="text-sm text-gray-600">{d}</p>
            </a>
          ))}
        </div>

        {/* Danger zone */}
        <div className="mt-12 pt-8 border-t border-[var(--omni-border)] flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold flex items-center gap-2 text-red-700"><Trash2 size={16} /> Zone de danger</h3>
            <p className="text-sm text-gray-500">La suppression du compte est définitive (à venir).</p>
          </div>
          <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="account-logout-btn" className="rounded-full">Se déconnecter</Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
